import { useState } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export default function ProgramSearch() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'programs' | 'professional'>('programs');

  const areas = [
    t('domain_all', 'Choisir un pilier...'),
    'Télécoms & Informatique',
    'Communication Numérique',
    'Ingénierie Énergétique',
    'Coaching Entrepreneurial'
  ];

  const levels = [
    t('campus_all', 'Sélectionner...'),
    'Palais des Congrès (Yaoundé)',
    'Formation Continue',
    'E-learning'
  ];

  return (
    <section className="bg-gray-50 py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Main Tabs - NSCC Style */}
        <div className="flex flex-col sm:flex-row">
          <button
            onClick={() => setActiveTab('programs')}
            className={`px-8 md:px-12 py-5 font-bold text-lg uppercase tracking-widest transition-all duration-300 flex-1 sm:flex-none ${
              activeTab === 'programs'
                ? 'bg-navy text-white'
                : 'bg-white text-navy hover:bg-gray-100 border-b sm:border-b-0 sm:border-r border-gray-200'
            }`}
          >
            {t('tab_search_programs', 'Recherche de Programme')}
          </button>
          <button
            onClick={() => setActiveTab('professional')}
            className={`px-8 md:px-12 py-5 font-bold text-lg uppercase tracking-widest transition-all duration-300 flex-1 sm:flex-none ${
              activeTab === 'professional'
                ? 'bg-navy text-white'
                : 'bg-white text-navy hover:bg-gray-100 border-b sm:border-b-0 sm:border-r border-gray-200'
            }`}
          >
            {t('tab_formation_pro', 'Formation Professionnelle')}
          </button>
        </div>

        {/* Search Panel Container */}
        <div className="bg-navy p-6 md:p-14 shadow-2xl relative animate-in fade-in zoom-in-95 duration-500">
          {/* Sub-Filters / Breadcrumb-like nav */}
          <div className="flex flex-wrap gap-4 md:gap-8 text-white text-[10px] md:text-xs font-black mb-10 uppercase tracking-[0.2em] items-center">
            <button className="underline decoration-gold decoration-2 underline-offset-8 transition-colors hover:text-gold">
              {t('filter_all', 'Tous les programmes')}
            </button>
            <span className="text-white/30 hidden md:inline">|</span>
            <button className="hover:text-gold transition-colors opacity-80 hover:opacity-100">
              {t('filter_bachelor', 'Bachelor (Licence)')}
            </button>
            <span className="text-white/30 hidden md:inline">|</span>
            <button className="hover:text-gold transition-colors opacity-80 hover:opacity-100">
              {t('filter_master', 'Master Professional')}
            </button>
            <span className="text-white/30 hidden md:inline">|</span>
            <button className="hover:text-gold transition-colors opacity-80 hover:opacity-100">
              {t('filter_doctorate', 'Doctorat (Co-tutelle)')}
            </button>
          </div>

          {/* Search Fields Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
            {/* Keyword Input */}
            <div className="flex flex-col gap-3 group">
              <label className="text-white text-[10px] font-black uppercase tracking-[0.25em] opacity-60 group-focus-within:opacity-100 transition-opacity">
                {t('label_keyword', 'Mot-clé')}
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder={t('placeholder_keyword', "Ex: Informatique, Télécoms...")}
                  className="w-full bg-white p-5 outline-none text-sm font-medium border-0 focus:ring-2 focus:ring-gold transition-shadow"
                />
              </div>
            </div>

            {/* Area of Excellence Select */}
            <div className="flex flex-col gap-3 group">
              <label className="text-white text-[10px] font-black uppercase tracking-[0.25em] opacity-60 group-focus-within:opacity-100 transition-opacity">
                {t('label_domain', "Domaine d'Excellence")}
              </label>
              <div className="relative">
                <select className="w-full bg-white p-5 outline-none appearance-none text-sm font-medium cursor-pointer border-0 focus:ring-2 focus:ring-gold">
                  {areas.map((area, idx) => (
                    <option key={idx} value={area}>{area}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-5 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none w-5 h-5" />
              </div>
            </div>

            {/* Campus/Level Select */}
            <div className="flex flex-col gap-3 group">
              <label className="text-white text-[10px] font-black uppercase tracking-[0.25em] opacity-60 group-focus-within:opacity-100 transition-opacity">
                {t('label_campus', 'Campus / Niveau')}
              </label>
              <div className="relative">
                <select className="w-full bg-white p-5 outline-none appearance-none text-sm font-medium cursor-pointer border-0 focus:ring-2 focus:ring-gold">
                  {levels.map((level, idx) => (
                    <option key={idx} value={level}>{level}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-5 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none w-5 h-5" />
              </div>
            </div>
          </div>

          {/* Action Bar */}
          <div className="mt-12 flex flex-wrap items-center gap-6 md:gap-10">
            <button className="bg-gold text-white px-12 md:px-20 py-4 font-bold text-lg uppercase tracking-widest shadow-lg hover:bg-gold/90 transition-all active:scale-95 flex items-center gap-3">
              <Search className="w-5 h-5" />
              {t('btn_rechercher', 'Rechercher')}
            </button>
            <button className="text-white underline text-sm font-bold hover:text-gold transition-colors underline-offset-4">
              {t('link_advanced_search', 'Options de recherche avancée')}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
